import { useNavigate, useLocation } from "react-router-dom";
import { ShieldOff, ArrowLeft, Home, Mail } from "lucide-react";

interface NoAccessProps {
  /**
   * Optional override — usually the page comes here via Navigate from
   * RequirePermission, which puts the required permission(s) on
   * `location.state` instead of props. The props are kept for direct callers
   * who may render <NoAccess /> manually.
   */
  requiredPermission?: string;
  requiredAnyOf?: string[];
}

interface RouterState {
  from?: string;
  requiredPermission?: string;
  requiredAnyOf?: string[];
}

export default function NoAccess({ requiredPermission, requiredAnyOf }: NoAccessProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as RouterState | null) || {};

  const reqOne = requiredPermission ?? state.requiredPermission;
  const reqAny = requiredAnyOf ?? state.requiredAnyOf;
  const fromPath = state.from;

  const missing = reqOne ? [reqOne] : reqAny || [];

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-10">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center shadow-md">
          <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <ShieldOff className="w-8 h-8 text-amber-600" />
          </div>

          <h1 className="text-5xl font-bold text-gray-900 mb-2">403</h1>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">
            You don't have access to this page
          </h2>
          <p className="text-sm text-gray-600 mb-6 leading-relaxed">
            Your current role doesn't include the permissions needed to view this page.
            If you think this is a mistake, contact your tenant administrator.
          </p>

          {fromPath && (
            <p className="text-[11px] text-gray-500 mb-4 font-mono break-all">
              Blocked: {fromPath}
            </p>
          )}

          {missing.length > 0 && (
            <div className="mb-6 text-left bg-gray-50 border border-gray-200 rounded-md p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
                {reqOne ? "Required permission" : "Required (any one)"}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {missing.map((p) => (
                  <span
                    key={p}
                    className="inline-block px-2 py-0.5 text-[11px] font-mono bg-amber-50 text-amber-700 rounded border border-amber-200"
                  >
                    {p}
                  </span>
                ))}
              </div>
              <p className="text-[11px] text-gray-500 mt-2">
                Share this with your admin so they can grant it.
              </p>
            </div>
          )}

          <div className="space-y-2">
            <button
              onClick={() => navigate(-1)}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Go back
            </button>
            <button
              onClick={() => navigate("/dashboard")}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-md text-sm font-medium"
            >
              <Home className="w-4 h-4" />
              Go to dashboard
            </button>
            <a
              href={`mailto:?subject=${encodeURIComponent("Access request for DocxIQ")}&body=${encodeURIComponent(
                `Hi Admin,\n\nI need access to a page in DocxIQ but my current role doesn't have the required permission.\n\n` +
                (fromPath ? `Page: ${fromPath}\n` : "") +
                (missing.length > 0 ? `Required: ${missing.join(", ")}\n` : "") +
                `\nThanks.`
              )}`}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-md text-xs font-medium"
            >
              <Mail className="w-3.5 h-3.5" />
              Email my admin to request access
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
